import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, TrendingUp, Users, FileText, Euro, Target, Mail, 
  Clock, CheckCircle, AlertCircle, Send, RefreshCw, Settings, 
  TestTube, ChevronDown, ChevronUp 
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const COLORS = ['#1e3a5f', '#3b82f6', '#f59e0b', '#ef4444'];
const CHART_COLORS = {
  primary: '#1e3a5f',
  blue: '#3b82f6',
  green: '#10b981',
  amber: '#f59e0b'
};

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [followUpStats, setFollowUpStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [debugQuotes, setDebugQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showFollowUps, setShowFollowUps] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    setRefreshing(true);
    try {
      const [statsRes, followUpRes, monthlyRes, emailRes] = await Promise.all([
        axios.get(`${API}/statistics`),
        axios.get(`${API}/statistics/follow-ups`),
        axios.get(`${API}/statistics/monthly`),
        axios.get(`${API}/debug/email-config`)
      ]);
      setStats(statsRes.data);
      setFollowUpStats(followUpRes.data);
      setMonthlyData(monthlyRes.data);
      setEmailConfig(emailRes.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
      // Reset refresh button after 1 second
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  const fetchDebugQuotes = async () => {
    try {
      const response = await axios.get(`${API}/debug/quotes`);
      setDebugQuotes(response.data);
      setShowDebug(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/debug/test-email`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du test');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Prepare pie chart data
  const pieData = [
    { name: 'Acceptés', value: stats.accepted_quotes, color: '#10b981' },
    { name: 'En attente', value: stats.sent_quotes, color: '#3b82f6' },
    { name: 'Brouillons', value: Math.max(0, stats.total_quotes - stats.sent_quotes - stats.accepted_quotes), color: '#94a3b8' }
  ].filter(item => item.value > 0);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name === 'CA' ? `${entry.value.toLocaleString()} €` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="statistics-title">Statistiques</h1>
          <p className="text-sm text-slate-500 mt-1">Analysez votre performance</p>
        </div>
        <Button 
          onClick={fetchAllStats} 
          variant={refreshing ? "default" : "outline"} 
          size="sm"
          disabled={refreshing}
          className={refreshing ? "bg-orange-500 hover:bg-orange-500 text-white" : ""}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> 
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </Button>
      </div>

      {/* Key Metrics - Mobile optimized grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="p-3 sm:p-5 bg-white border border-slate-200/50 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{stats.total_clients}</p>
              <p className="text-xs text-slate-500 truncate">Clients</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-5 bg-white border border-slate-200/50 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{stats.total_quotes}</p>
              <p className="text-xs text-slate-500 truncate">Devis total</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-5 bg-white border border-slate-200/50 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{stats.accepted_quotes}</p>
              <p className="text-xs text-slate-500 truncate">Acceptés</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-5 bg-white border border-slate-200/50 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{stats.conversion_rate.toFixed(0)}%</p>
              <p className="text-xs text-slate-500 truncate">Conversion</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Signature Time Card - NEW */}
      {stats.avg_signature_time && (
        <Card className="p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-indigo-600 font-medium">Délai moyen de signature</p>
                <p className="text-2xl font-bold text-indigo-900">{stats.avg_signature_time}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-indigo-500">Temps entre envoi et signature</p>
            </div>
          </div>
        </Card>
      )}

      {/* Marge Brute Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-emerald-600 font-medium">Marge brute moyenne</p>
              <p className="text-2xl font-bold text-emerald-900">
                {(stats.marge_brute_moyenne || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </p>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-emerald-200">
            <p className="text-xs text-emerald-600">
              {(stats.marge_brute_moyenne_percent || 0).toFixed(1)}% du CA HT moyen
            </p>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-blue-600 font-medium">Marge brute totale</p>
              <p className="text-2xl font-bold text-blue-900">
                {(stats.marge_brute_totale || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </p>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-blue-200">
            <p className="text-xs text-blue-600">
              Sur {stats.accepted_quotes} chantiers acceptés
            </p>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-amber-600 font-medium">Fournitures totales</p>
              <p className="text-2xl font-bold text-amber-900">
                {(stats.total_fournitures || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </p>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-amber-200">
            <p className="text-xs text-amber-600">
              Coût matériaux & fournitures
            </p>
          </div>
        </Card>
      </div>

      {/* Marge par chantier - Table */}
      {stats.margins_by_project && stats.margins_by_project.length > 0 && (
        <Card className="p-4 sm:p-6 bg-white border border-slate-200/50 shadow-sm">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Marge brute par chantier
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-slate-600 font-medium">Devis</th>
                  <th className="text-left py-2 px-2 text-slate-600 font-medium">Client</th>
                  <th className="text-right py-2 px-2 text-slate-600 font-medium">Total HT</th>
                  <th className="text-right py-2 px-2 text-slate-600 font-medium">Fournitures</th>
                  <th className="text-right py-2 px-2 text-slate-600 font-medium">Marge</th>
                  <th className="text-right py-2 px-2 text-slate-600 font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {stats.margins_by_project.map((project, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-mono text-xs">{project.quote_number}</td>
                    <td className="py-2 px-2 truncate max-w-[120px]">{project.client_name}</td>
                    <td className="py-2 px-2 text-right">{project.total_ht?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</td>
                    <td className="py-2 px-2 text-right text-amber-600">{project.fournitures?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</td>
                    <td className="py-2 px-2 text-right font-semibold text-emerald-600">{project.marge_brute?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        project.marge_percent >= 50 ? 'bg-emerald-100 text-emerald-700' :
                        project.marge_percent >= 30 ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {project.marge_percent?.toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Charts Section - Mobile stack, Desktop side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pie Chart - Quote Distribution */}
        <Card className="p-4 sm:p-6 bg-white border border-slate-200/50 shadow-sm">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Répartition des devis
          </h2>
          {pieData.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-full sm:w-1/2 h-48 sm:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full sm:w-1/2 space-y-2">
                {pieData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-sm text-slate-700">{item.name}</span>
                    </div>
                    <span className="font-bold text-slate-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400">
              Aucune donnée disponible
            </div>
          )}
        </Card>

        {/* Revenue Card */}
        <Card className="p-4 sm:p-6 bg-white border border-slate-200/50 shadow-sm">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Chiffre d'affaires
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total TTC</p>
              <p className="text-3xl sm:text-4xl font-bold text-primary">
                {stats.total_revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">TVA collectée</p>
                <p className="text-lg font-bold text-slate-900">
                  {stats.tva_collected.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Panier moyen</p>
                <p className="text-lg font-bold text-slate-900">
                  {stats.accepted_quotes > 0 
                    ? (stats.total_revenue / stats.accepted_quotes).toLocaleString('fr-FR', { minimumFractionDigits: 0 })
                    : 0} €
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Monthly Evolution Charts */}
      <Card className="p-4 sm:p-6 bg-white border border-slate-200/50 shadow-sm">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          Évolution mensuelle
        </h2>
        
        {monthlyData.length > 0 ? (
          <div className="space-y-6">
            {/* Quotes Bar Chart */}
            <div>
              <p className="text-sm text-slate-500 mb-3">Devis créés vs acceptés</p>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#64748b" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="quotes" name="Créés" fill={CHART_COLORS.blue} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="accepted" name="Acceptés" fill={CHART_COLORS.green} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue Line Chart */}
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-3">Chiffre d'affaires mensuel</p>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#64748b" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748b" tickFormatter={(value) => `${value}€`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      name="CA" 
                      stroke={CHART_COLORS.primary} 
                      strokeWidth={3}
                      dot={{ fill: CHART_COLORS.primary, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: CHART_COLORS.primary }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-slate-400">
            Pas assez de données pour afficher les graphiques
          </div>
        )}
      </Card>

      {/* Follow-up Statistics - Collapsible */}
      {followUpStats && (
        <Card className="bg-white border border-slate-200/50 shadow-sm overflow-hidden">
          <div 
            className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => setShowFollowUps(!showFollowUps)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900">Statistiques de Relances</h2>
                  <p className="text-xs sm:text-sm text-slate-500">Emails J+3 et J+7</p>
                </div>
              </div>
              {showFollowUps ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
          
          {showFollowUps && (
            <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Relances envoyées */}
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                    <Send className="w-3 h-3" /> Envoyées
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">J+3</span>
                      <span className="font-bold text-slate-900">{followUpStats.follow_ups?.j3_sent || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">J+7</span>
                      <span className="font-bold text-slate-900">{followUpStats.follow_ups?.j7_sent || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-primary/10 rounded-lg">
                      <span className="text-sm font-medium text-primary">Total</span>
                      <span className="font-bold text-primary">{followUpStats.follow_ups?.total_sent || 0}</span>
                    </div>
                  </div>
                </div>

                {/* En attente */}
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                    <Clock className="w-3 h-3" /> En attente
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center p-2.5 bg-amber-50 rounded-lg">
                      <span className="text-sm text-amber-700">J+3</span>
                      <span className="font-bold text-amber-700">{followUpStats.follow_ups?.pending_j3 || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-red-50 rounded-lg">
                      <span className="text-sm text-red-700">J+7</span>
                      <span className="font-bold text-red-700">{followUpStats.follow_ups?.pending_j7 || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-blue-50 rounded-lg">
                      <span className="text-sm text-blue-700">Signature</span>
                      <span className="font-bold text-blue-700">{followUpStats.pending_signature || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Performance */}
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Performance
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center p-2.5 bg-emerald-50 rounded-lg">
                      <span className="text-sm text-emerald-700">Convertis</span>
                      <span className="font-bold text-emerald-700">{followUpStats.conversion?.accepted_after_followup || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-emerald-50 rounded-lg">
                      <span className="text-sm text-emerald-700">Taux</span>
                      <span className="font-bold text-emerald-700">{followUpStats.conversion?.rate_after_followup || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-purple-50 rounded-lg">
                      <span className="text-sm text-purple-700">Délai moy.</span>
                      <span className="font-bold text-purple-700">{followUpStats.conversion?.avg_signature_days || 0}j</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Email Config & Debug - Collapsible */}
      <Card className="bg-white border border-slate-200/50 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-slate-900">Configuration & Debug</h2>
              <p className="text-xs sm:text-sm text-slate-500">Vérifiez votre configuration</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`p-3 sm:p-4 rounded-lg border ${emailConfig?.smtp_configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {emailConfig?.smtp_configured ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                )}
                <span className={`text-sm font-medium ${emailConfig?.smtp_configured ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {emailConfig?.smtp_configured ? 'SMTP configuré' : 'SMTP non configuré'}
                </span>
              </div>
              <div className="text-xs space-y-0.5 text-slate-600">
                <p><strong>Serveur:</strong> {emailConfig?.smtp_server}</p>
                <p><strong>Port:</strong> {emailConfig?.smtp_port}</p>
                {emailConfig?.fallback && (
                  <p className="text-amber-600"><strong>Fallback:</strong> {emailConfig?.fallback}</p>
                )}
              </div>
            </div>
            
            <div className="flex flex-col justify-center items-center gap-2 p-3">
              <Button 
                onClick={handleTestEmail} 
                disabled={testingEmail}
                className="w-full"
                size="sm"
                variant={emailConfig?.smtp_configured ? 'default' : 'outline'}
              >
                <TestTube className="w-4 h-4 mr-2" />
                {testingEmail ? 'Envoi...' : 'Tester l\'email'}
              </Button>
            </div>
          </div>

          {/* Debug Quotes Table */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-700">Debug - Devis</h3>
              <Button onClick={fetchDebugQuotes} variant="outline" size="sm">
                {showDebug ? 'Actualiser' : 'Charger'}
              </Button>
            </div>
            
            {showDebug && debugQuotes.length > 0 && (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-xs sm:text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 sm:px-3 font-medium text-slate-600">N°</th>
                      <th className="text-left py-2 px-2 sm:px-3 font-medium text-slate-600">Client</th>
                      <th className="text-left py-2 px-2 sm:px-3 font-medium text-slate-600">Statut</th>
                      <th className="text-right py-2 px-2 sm:px-3 font-medium text-slate-600">Montant</th>
                      <th className="text-center py-2 px-2 sm:px-3 font-medium text-slate-600">J3</th>
                      <th className="text-center py-2 px-2 sm:px-3 font-medium text-slate-600">J7</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugQuotes.slice(0, 8).map((quote) => (
                      <tr key={quote.quote_id} className="border-b border-slate-100">
                        <td className="py-2 px-2 sm:px-3 font-mono">{quote.quote_number?.slice(-4)}</td>
                        <td className="py-2 px-2 sm:px-3 truncate max-w-[100px]">{quote.client_name}</td>
                        <td className="py-2 px-2 sm:px-3">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            quote.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                            quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="py-2 px-2 sm:px-3 text-right">{quote.total_ttc?.toFixed(0)}€</td>
                        <td className="py-2 px-2 sm:px-3 text-center">
                          {quote.follow_up_3d_sent ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-300 mx-auto" />
                          )}
                        </td>
                        <td className="py-2 px-2 sm:px-3 text-center">
                          {quote.follow_up_7d_sent ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-300 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Summary Banner */}
      <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary via-primary to-primary/90 shadow-lg">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative p-4 sm:p-6 text-white">
          <h2 className="text-lg sm:text-xl font-bold mb-4">Résumé</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            <div className="text-center">
              <p className="text-white/70 text-xs sm:text-sm mb-1">Devis</p>
              <p className="text-2xl sm:text-4xl font-bold">{stats.total_quotes}</p>
            </div>
            <div className="text-center">
              <p className="text-white/70 text-xs sm:text-sm mb-1">Clients</p>
              <p className="text-2xl sm:text-4xl font-bold">{stats.total_clients}</p>
            </div>
            <div className="text-center">
              <p className="text-white/70 text-xs sm:text-sm mb-1">CA</p>
              <p className="text-2xl sm:text-4xl font-bold">{(stats.total_revenue/1000).toFixed(0)}k€</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Statistics;
